import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatSlideToggle,
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import {
  DeviceModel,
  MeterRole,
  PvForecastingConfig,
  PvForecastingConfigUpdate,
  effectiveMeterRole,
} from '@core/models';
import { PvForecastingService } from '@core/services';
import { CardComponent } from '@custom-components/general/card/card.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceStore } from '@root/app/store/device.store';
import { PvForecastingStore } from '@root/app/store/pv-forecasting.store';
import {
  PvForecastDialogComponent,
  PvForecastDialogData,
} from '../pv-forecast-dialog/pv-forecast-dialog.component';
import {
  PvForecastEmeterChannel,
  wattPeakToKilowattPeak,
} from '../pv-forecast-form/pv-forecast-form';
import {
  AZIMUTH_PRESETS,
  PresetOption,
  TILT_PRESETS,
  presetLabelKeyFor,
} from '../pv-forecast-presets';

/** Backend category name for energy meters (the "emeter" channels). */
const EMETER_CATEGORY = 'Energy Meter';

@Component({
  selector: 'xemex-pv-forecast-card',
  imports: [MatSlideToggleModule, CardComponent, TranslateModule],
  templateUrl: './pv-forecast-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PvForecastCardComponent {
  deviceDetail = input<DeviceModel>();
  partnerDefaultMeterRole = input<MeterRole>();

  protected readonly store = inject(PvForecastingStore);
  private readonly deviceStore = inject(DeviceStore);
  private readonly pvForecastingService = inject(PvForecastingService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly toggle = viewChild<MatSlideToggle>('toggle');

  protected readonly isSaving = signal(false);
  protected readonly active = computed(() => this.store.config()?.active ?? false);

  /**
   * A yield forecast only means something for a meter that measures production, so only a
   * production-role meter may have forecasting switched on. That is the effective role the meter
   * role select shows: a device that stores none takes its partner's default.
   *
   * Turning it *off* stays possible regardless: a meter whose role changed after the fact
   * would otherwise be stuck with an active forecast nobody can reach.
   */
  protected readonly canEnable = computed(
    () =>
      effectiveMeterRole(this.deviceDetail()?.meterRole, this.partnerDefaultMeterRole()) ===
      'production',
  );
  protected readonly enableBlocked = computed(() => !this.canEnable() && !this.active());

  /**
   * The card renders before the parent has resolved the device, and the PV-config loader does not
   * cover that gap. Until the device is in, its meter role is unknown rather than wrong, so the
   * toggle stays disabled (there is no device to act on) but the "change the meter role first"
   * explanation is withheld — it would otherwise accuse a production meter of being the wrong one.
   */
  protected readonly wrongMeterRole = computed(
    () => this.deviceDetail() !== undefined && this.enableBlocked(),
  );

  /** Energy-meter channels the user can pick as the forecast's `device_index`. */
  protected readonly emeterChannels = computed<PvForecastEmeterChannel[]>(() => {
    const configured = (this.deviceStore.channelConfiguration()?.channels ?? [])
      .filter(channel => channel.category === EMETER_CATEGORY)
      .map(channel => ({ channelNumber: channel.channelNumber, description: channel.description }));

    // A DLMS device is itself an emeter at device_index 0 and is always
    // available; any configured emeter channel counts as an additional one.
    if (this.deviceDetail()?.protocol === 'DLMS') {
      return [{ channelNumber: 0, description: '', isDeviceMeter: true }, ...configured];
    }
    return configured;
  });

  private lastLoadedDeviceId: string | undefined;

  constructor() {
    effect(() => {
      const deviceId = this.deviceDetail()?.deviceId;
      if (!deviceId || this.lastLoadedDeviceId === deviceId) return;

      this.lastLoadedDeviceId = deviceId;
      this.store.getConfigMethod(Number(deviceId));
      // The emeter picker needs the device's channels; load them if the
      // Configuration tab hasn't already populated the (shared) device store.
      if (!untracked(() => this.deviceStore.channelConfiguration())) {
        this.deviceStore.getChannelConfigurationMethod(deviceId);
      }
    });
  }

  protected onToggleChange(event: MatSlideToggleChange): void {
    const deviceId = this.deviceId();
    if (deviceId === undefined) {
      this.syncToggle();
      return;
    }

    const config = this.store.config();
    if (event.checked) {
      // No config yet → collect details first; otherwise just re-activate.
      config ? this.setActive(deviceId, config, true) : this.openDialog('create');
    } else if (config) {
      this.setActive(deviceId, config, false);
    }
  }

  protected openEditDialog(): void {
    const config = this.store.config();
    if (config) {
      this.openDialog('edit', config);
    }
  }

  /** Named tilt preset (e.g. "Medium (15-30°)") for a stored value, or the raw "23°" if custom. */
  protected tiltDisplay(value: number): string {
    return this.presetDisplay(TILT_PRESETS, value);
  }

  protected kwpDisplay(wp: number): number {
    return wattPeakToKilowattPeak(wp);
  }

  /** Named orientation preset (e.g. "South (180°)") for a stored value, or the raw "200°" if custom. */
  protected azimuthDisplay(value: number): string {
    return this.presetDisplay(AZIMUTH_PRESETS, value);
  }

  private presetDisplay(presets: PresetOption[], value: number): string {
    const labelKey = presetLabelKeyFor(presets, value);
    return labelKey ? this.translate.instant(labelKey) : `${value}°`;
  }

  private deviceId(): number | undefined {
    const id = this.deviceDetail()?.deviceId;
    return id ? Number(id) : undefined;
  }

  private setActive(deviceId: number, config: PvForecastingConfig, active: boolean): void {
    const body: PvForecastingConfigUpdate = {
      device_index: config.device_index,
      device_type: config.device_type,
      active,
      pv_arrays: config.pv_arrays,
    };

    this.isSaving.set(true);
    this.pvForecastingService
      .updateConfig(deviceId, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: updated => {
          this.store.setConfig(updated);
          this.isSaving.set(false);
          this.notify('devices.pvForecast.saved');
        },
        error: () => {
          this.isSaving.set(false);
          this.notify('devices.pvForecast.genericError');
          this.syncToggle();
        },
      });
  }

  private openDialog(mode: 'create' | 'edit', config?: PvForecastingConfig): void {
    const deviceId = this.deviceId();
    if (deviceId === undefined) return;

    const ref = this.dialog.open(PvForecastDialogComponent, {
      panelClass: 'no-padding-dialog',
      width: '40rem',
      maxWidth: 'calc(100vw - 2rem)',
      data: {
        mode,
        deviceId,
        emeterChannels: this.emeterChannels(),
        config,
      } satisfies PvForecastDialogData,
    });

    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: PvForecastingConfig) => {
        if (result) {
          this.store.setConfig(result);
          this.notify('devices.pvForecast.saved');
        } else {
          // Cancelled (or dialog handled its own error) — revert to persisted state.
          this.syncToggle();
        }
      });
  }

  private notify(messageKey: string): void {
    this.snackBar.open(this.translate.instant(messageKey), '', {
      horizontalPosition: 'right',
      verticalPosition: 'top',
      duration: 3000,
    });
  }

  /** Forces the toggle back to the stored truth (the [checked] binding alone
   *  won't re-apply when the value is unchanged, e.g. after a cancel). */
  private syncToggle(): void {
    const toggle = this.toggle();
    if (toggle) {
      toggle.checked = this.active();
    }
  }
}
